/*
  •	Copyright (c) 2023 Pegasystems Inc.
  •	All rights reserved.
*/
import merge from 'lodash.mergewith';
import get from 'lodash.get';
import type { AxiosResponseHeaders, RawAxiosResponseHeaders } from 'axios';

import { publishResponse } from '../../../observables/stream-observable';
import { parseResponse, getSemanticURL } from '../../../router/app-router';
import { getResolvedTargetName } from '../../../router/routing-manager';
import loadComponents from '../../../utils/component-loader';
import constants, { publicConstants } from '../../../constants';
import * as ContainerUtils from '../../../container/container-utils';
import Utils from '../../../utils/utils';
import NavigationUtils from '../../../utils/navigation-utils';
import * as actionType from '../actions';
import * as actions from '../actions/action-creators';
import { ActionManagerInstance } from '../../../actions/manager';
import { getFormContent, getVisibleFormFields } from '../../../utils/form-handler';
import {
  getChanges,
  isContextDirty,
  FORM_DIRTY_MESSAGE,
  MAX_DOCUMENT_LIMIT_REACHED
} from '../middlewares/property-change-middleware';
import { buildContextName, getParentContextName } from '../../../utils/context-utils';
import {
  isContainerSingleMode,
  hasContainerItems,
  isContainerMultipleMode,
  getActiveContainerItemContext,
  getActiveContainerItemName
} from '../../../container/container-utils';
import { ACTION_OPENASSIGNMENT, ADD_CONTAINER_ITEM } from '../actions';
import { getInstructions, getPageInstructionReferences } from '../../../page-instructions';
import RemoteCase from '../../../case/remote-case';
import * as StateManager from '../state-manager';
import { getContainerNameFromContainerItemID, getDisplayCaseID, isCoexistenceCase } from '../../../case/common-helper';
import { getLocaleUtils } from '../../../locale/locale-utils';
import { getPropertyMetadata } from '../../../rules/rules-store';
import EnvironmentInfo from '../../../environment-info/index';
import type { EditableFormFieldOptions } from '../../../utils/types';
import type { ActionObject, ExtendedActionObject } from '../../../globals';
import type { DxAPIResponseObject, RemoteCaseInfo } from '../../../case/types';
import type { PropertyModel } from '../../../rules/types';
import CaseUtils from '../../../case/utils';
import type { ErrorMessages } from '../reducers/data/types';
import type { Actionout } from '../../../types';
import StateUtils from '../utils/state-utils';

const { RESOURCE_TYPES } = publicConstants;

export const SCOPE_TYPES = {
  PAGE: 'page',
  FORM: 'form'
};

const RESOURCE_TYPE_ACTIONS_MAP = {
  [RESOURCE_TYPES.CASE]: [
    actionType.ACTION_CREATEWORK,
    actionType.ACTION_OPENWORKBYHANDLE,
    actionType.ACTION_OPENASSIGNMENT
  ],
  [RESOURCE_TYPES.OBJECT]: [
    actionType.ACTION_CREATEOBJECT,
    actionType.ACTION_GETOBJECT,
    actionType.ACTION_CREATE_EXTERNAL_OBJECT
  ],
  [RESOURCE_TYPES.PAGE]: [actionType.ACTION_SHOWVIEW, actionType.ACTION_SHOWINSIGHT],
  [RESOURCE_TYPES.DATA]: [actionType.ACTION_SHOWDATA],
  ASSIGNMENT: [actionType.ACTION_LAUNCH_ASSIGNMENT]
};

export const processHeaders = (
  responseHeaders: RawAxiosResponseHeaders | AxiosResponseHeaders,
  caseClassName?: string,
  optionalHeaders: { [key: string]: any } = {}
) => {
  if (!responseHeaders) return {};
  const headers: ActionObject['headers'] = {
    etag: responseHeaders.etag
  };
  const { RemoteSystemID } = optionalHeaders;
  if (
    responseHeaders[publicConstants.REMOTE_SYSTEM_ID] ||
    responseHeaders[publicConstants.REMOTE_SYSTEM_ID.toLowerCase()] ||
    RemoteSystemID
  ) {
    headers.RemoteSystemID =
      responseHeaders[publicConstants.REMOTE_SYSTEM_ID] ||
      responseHeaders[publicConstants.REMOTE_SYSTEM_ID.toLowerCase()] ||
      RemoteSystemID;
  } else if (caseClassName && RemoteCase.isRemoteCase(caseClassName)) {
    headers.RemoteSystemID = RemoteCase.getRemoteHeader(caseClassName);
  }
  return headers;
};

/**
 * This function converts Object string in path to array path for lodash unset
 * @param str path to be converted
 * @returns converted string
 */
/*
'content.Property.AdditionalAttributes[0].abb.Nested[2]' -->
"['content']['Property']['AdditionalAttributes']"

*/
export const convertLiteralKey = (str: string) => {
  if (!str) return '';
  const arr = str.split('.');
  let removerReference = '';
  let token: string;
  for (token of arr) {
    if (token) {
      if (token.includes('[') && token.includes(']')) {
        removerReference += `['${token.substring(0, token.lastIndexOf('['))}']`;
        return removerReference;
      }
      removerReference += `['${token}']`;
    }
  }

  return removerReference;
};

const getSubmittableContent = (
  isTransientContext: boolean,
  submitData?: { caseInfo: { [key: string]: any }; objectInfo: { [key: string]: any } },
  isObjectWideAction = false,
  isExternalObject = false
) => {
  let content: object = {};
  const info = isObjectWideAction || isExternalObject ? submitData?.objectInfo : submitData?.caseInfo;
  if (isTransientContext || info) {
    content = !isTransientContext ? { ...info } : { ...submitData };
  }
  return { content };
};

const removePropertiesFromContent = (
  formContent: {
    [key: string]: {
      [key: string]: { [key: string]: string };
    };
  },
  context: string
) => {
  Object.entries(formContent?.caseInfo?.content).forEach(([key, value]) => {
    const contextData = StateManager.getDataObject(context);
    const classID = get(contextData, publicConstants.CASE_INFO.CASE_INFO_CLASSID);
    const propertyMeta = getPropertyMetadata(key, classID) as PropertyModel;
    if ((propertyMeta?.type === 'True-False' && !value) || value === '') {
      delete formContent.caseInfo.content[key];
    }
  });
};

/**
 * This function return form data for a given context
 * @param context context of form
 * @param options details like onlyChangedData
 * @param options.onlyChangedData if true, getSubmitData returns only edited fields data from the form
 * @param options.nonEmptyAndChangedData if true, getSubmitData returns pre-populated nonEmpty data and data edited by the user
 * @param options.skipUUID if true, returns pageInstructions by deleting the UUID property from content (currently it is - "EmbedListUUID__")
 * @param options.isTransientContext if true, indicates transient Context to include submittable data from first/top level inside of context
 * @param options.isObjectWideAction if true, fetches content from objectInfo
 * @param {boolean} [options.includeDisabledFields] if true, indicates disabled fields inside of context.
 * @returns form data
 */
export const getSubmitData = (context: string, options: EditableFormFieldOptions = {}) => {
  const {
    onlyChangedData = false,
    nonEmptyAndChangedData = false,
    skipUUID = true,
    isTransientContext = false,
    includeDisabledFields = false,
    isObjectWideAction,
    isExternalObject
  } = options;
  let formContent: { [key: string]: any } = {};
  formContent = getFormContent(context, {
    isTransientContext,
    includeDisabledFields,
    isObjectWideAction,
    isExternalObject
  });
  if (onlyChangedData) {
    delete formContent?.caseInfo?.content;
    if (isTransientContext) {
      formContent = {};
    }
  }
  const changes = getChanges(context, false);

  if (!isTransientContext && nonEmptyAndChangedData) {
    removePropertiesFromContent(formContent, context);
  }

  const submitData = merge(changes, formContent);
  let content: { [key: string]: any } = {};
  ({ content } = getSubmittableContent(isTransientContext, submitData, isObjectWideAction, isExternalObject));

  const instructionsForCurrentContext = getInstructions(context);
  const copiedInstructionsForCurrentContext = [...instructionsForCurrentContext];

  if (skipUUID) {
    copiedInstructionsForCurrentContext.forEach((instruction) => {
      delete instruction.content?.EmbedListUUID__;
    });
  }
  content.pageInstructions = copiedInstructionsForCurrentContext;

  const references = getPageInstructionReferences(context);
  references.forEach((ref: string) => {
    if (content?.content) {
      const target = ref?.replace(constants.getProperty(constants.CASE_INFO.CASE_INFO_CONTENT), '');
      Utils.deleteIn(content.content, convertLiteralKey(target));
    }
  });
  if (!isTransientContext && submitData?.dataInfo) {
    content = { ...submitData.dataInfo, pageInstructions: content.pageInstructions };
    return { content };
  }
  const additionalPayload = StateManager.getValue(
    '.pzAdditionalPayload',
    constants.CASE_INFO.CASE_INFO_CONTENT,
    context
  );
  if (content.content && additionalPayload) {
    content.content.pzAdditionalPayload = additionalPayload;
  }
  return { content };
};

export const getActionPayload = (translatedDXAPIResponse: DxAPIResponseObject, commonInfo: any) => {
  let contextData;
  let root: object | undefined;
  let actionButtons = {};
  const uiResources = Utils.getIn(translatedDXAPIResponse, constants.UI_RESOURCES);
  const confirmationNote = Utils.getIn(translatedDXAPIResponse, 'confirmationNote');
  if (uiResources) {
    contextData = Utils.getIn(translatedDXAPIResponse, constants.UI_RESOURCES_CONTEXT_DATA);
    root = Utils.getIn(translatedDXAPIResponse, constants.ROOT);
    actionButtons = Utils.getIn(translatedDXAPIResponse, constants.ACTION_BUTTONS);
  } else if (confirmationNote) {
    root = {};
  }

  const { data = {}, Errors: errors, flowName } = translatedDXAPIResponse;
  let { semanticURL, key } = translatedDXAPIResponse;
  const objectInfoID = data?.objectInfo?.ID;
  if (objectInfoID) {
    key = key || objectInfoID;
    semanticURL = semanticURL || `objects/${objectInfoID}`;
  }
  const nextAssignmentInfo = Utils.getIn(translatedDXAPIResponse, 'nextAssignmentInfo', null);
  const assignmentContext = Utils.getIn(translatedDXAPIResponse, 'nextAssignmentInfo.context', '');
  const confirmationLinks = Utils.getIn(translatedDXAPIResponse, 'confirmationLinks', null);

  const { isInCreateStage, openCaseViewAfterCreate, debugInfo, isExternalObjectCreationFlow } = commonInfo;
  return {
    data: {
      ...data,
      key,
      flowName,
      coreHeaders: debugInfo && {
        debugInfo: {
          [constants.COREHEADER_APP_DEBUG_ID]: debugInfo.debugId
        }
      },
      [constants.getProperty(constants.CASE_INFO.CASE_INFO)]: {
        ...data[constants.getProperty(constants.CASE_INFO.CASE_INFO) as keyof typeof data],
        actionButtons,
        headers: commonInfo.headers
      },
      [constants.CONTEXT_DATA]: isInCreateStage
        ? {
            ...data[constants.CONTEXT_DATA as keyof typeof data],
            ...contextData,
            openCaseViewAfterCreate,
            errors,
            isExternalObjectCreationFlow
          }
        : { ...data[constants.CONTEXT_DATA as keyof typeof data], ...contextData, errors }
    },
    semanticURL,
    nextAssignmentInfo,
    assignmentContext,
    confirmationLinks,
    key,
    root,
    ...commonInfo
  };
};

export const processViewResponse = async (
  translatedDXAPIResponse: DxAPIResponseObject,
  actionConfig: { [key: string]: any },
  options?: {
    acName?: string | null;
    context?: string | null;
    target?: string | null;
    containerName?: string;
    containerItemID?: string;
  },
  isSemanticURLAssociatedWithActivateContainer = false,
  skipParseResponse = false
) => {
  const components = Utils.getIn(translatedDXAPIResponse, constants.COMPONENTS);
  if (components) {
    try {
      await loadComponents(components);
    } catch (error) {
      console.error(error);
    }
  }
  const optionsData = { actionConfig, ...options };
  if (isSemanticURLAssociatedWithActivateContainer) {
    parseResponse({ ...translatedDXAPIResponse, options: optionsData, directUrlUpdation: true });
  } else {
    publishResponse(translatedDXAPIResponse, optionsData, skipParseResponse);
  }
};

/**
 * this function returns the work id from the insKey
 * @private
 * @function getIdFromInsKey
 * @param insKey
 * @static
 *
 * @example  <caption>Example for getIdFromInsKey.</caption>
 * getIdFromInsKey('ASSIGN-WORKLIST ON8TTL-GALLERY-WORK C-5028!DETAILS_FLOW');
 * example will return C-5028
 */

export const getIdFromInsKey = (insKey: string) => {
  const result = insKey && insKey.match(/\S+(?=!)/);
  return result ? result[0] : '';
};

/**
 * this function returns the work id from the insKey
 * @private
 * @function getWorkIDFromInsKey
 * @param insKey inskey
 * @returns case id
 * @static
 *
 * @example  <caption>Example for getIdFromInsKey.</caption>
 * getIdFromInsKey('ASSIGN-WORKLIST ON8TTL-GALLERY-WORK C-5028!DETAILS_FLOW');
 * example will return v
 */
export const getWorkIDFromInsKey = (insKey = '') => {
  const startIndex = insKey.indexOf(' ');
  const endIndex = insKey.indexOf('!');
  return insKey.substring(startIndex + 1, endIndex);
};

/**
 * this function returns the assignment key and flow name from insKey
 * @private
 * @function getKeyAndFlowName
 * @param insKey insKey
 * @returns key and flow name of case
 * @static
 *
 * @example  <caption>Example for getKeyAndFlowName.</caption>
 * getKeyAndFlowName('ASSIGN-WORKLIST ON8TTL-GALLERY-WORK C-5028!DETAILS_FLOW');
 * example will return
 * {
 *  key: 'ASSIGN-WORKLIST ON8TTL-GALLERY-WORK C-5028',
 *  flowName: DETAILS_FLOW
 * }
 */
export const getKeyAndFlowName = (insKey?: string) => {
  if (insKey) {
    const [, flowName] = insKey.split('!');
    const key = getWorkIDFromInsKey(insKey);
    return { key, flowName };
  }
  return {};
};

/**
 * this function returns if the container item needs to be replaced
 * @private
 * @function shouldReplaceActiveItem
 * @param currentKey current item key
 * @param target target container
 * @returns returns true if container item needs to be replaced, otherwise returns false
 * @static
 *
 * @example  <caption>Example for getKeyAndFlowName.</caption>
 * shouldReplaceActiveItem('ON8TTL-GALLERY-WORK C-5028', 'app/primary_1/workarea');
 */
export const shouldReplaceActiveItem = (currentKey: string, target: string | null) => {
  const activeContainerItemName = getActiveContainerItemName(target);
  let activeContainerItemInfo: { key: string } | null = null;
  if (activeContainerItemName) {
    activeContainerItemInfo = ContainerUtils.getContainerItemData(target, activeContainerItemName);
  }
  return activeContainerItemInfo?.key === currentKey && isContainerMultipleMode(target);
};

const processErrors = (
  err: { [x: string]: string | any[] },
  formFields: string | string[],
  pageErrors: any[],
  fieldErrors: any[]
) => {
  switch (true) {
    // Embedded page errors
    case Boolean(err[constants.ERROR_IN_PAGE_IDENTIFIER] && err[constants.ERROR_FIELD_IN_PAGE]): {
      const embeddedErrorField = `${err[constants.ERROR_FIELD_IN_PAGE]}`.replace(
        /(\(\d+\))/g,
        (str) => `[${parseInt(str.substring(1, str.length - 1), 10) - 1}]`
      );
      const indexedEmbeddedErrorField = `${embeddedErrorField}${err[constants.ERROR_IN_PAGE_IDENTIFIER]}`;
      const cloneErr = Utils.deepClone(err);
      cloneErr[constants.ERROR_FIELD_IN_PAGE] = embeddedErrorField;
      if (formFields.includes(indexedEmbeddedErrorField.slice(1))) {
        fieldErrors.push(cloneErr);
      } else if (
        formFields.includes(
          indexedEmbeddedErrorField.substring(
            indexedEmbeddedErrorField.indexOf('.') + 1,
            indexedEmbeddedErrorField.indexOf('[')
          )
        ) ||
        formFields.includes(
          indexedEmbeddedErrorField.substring(
            indexedEmbeddedErrorField.indexOf('.') + 1,
            indexedEmbeddedErrorField.indexOf('(')
          )
        )
      ) {
        fieldErrors.push(cloneErr);
      } else {
        pageErrors.push(err);
      }
      break;
    }
    // Field level errors
    case Boolean(err[constants.ERROR_IN_PAGE_IDENTIFIER] && !err[constants.ERROR_FIELD_IN_PAGE]): {
      const errorField: any = err[constants.ERROR_IN_PAGE_IDENTIFIER].slice(1);
      if (formFields.includes(errorField)) {
        fieldErrors.push(err);
      } else {
        pageErrors.push(err);
      }
      break;
    }
    // Page level errors
    default:
      pageErrors.push(err);
      break;
  }
};

/**
 * Process the Error response and separate out field level errors, page level errors & (@TODO)app level errors,
 * @param error error content
 * @param formFields
 * ErroneousInputOutputIdentifier --> field which has error
 * ErroneousInputOutputFieldInPage --> Decides whether it is page level or field level error.
 */
export const processErrorResponse = (errors: ErrorMessages[], formFields: any) => {
  const fieldOrderMap: Map<string, number> = new Map(formFields.map((f: string, i: number) => [f, i]));

  const resolveField = (err: ErrorMessages): string => {
    const page = (err.erroneousInputOutputFieldInPage || '').replace(/^\./, '');
    const field = (err.erroneousInputOutputIdentifier || '').replace(/^\./, '');
    return page ? `${page}.${field}` : field;
  };

  const getFieldIndex = (err: ErrorMessages): number => fieldOrderMap.get(resolveField(err)) ?? Number.MAX_SAFE_INTEGER;

  const sortedErrors = errors.slice().sort((a, b) => getFieldIndex(a) - getFieldIndex(b));

  const pageErrors: never[] = [];
  const fieldErrors: ErrorMessages[] = [];

  // Separating out the app (@TODO), thread, field level errors
  for (const err of sortedErrors) {
    processErrors(err, formFields, pageErrors, fieldErrors);
  }

  return { fieldErrors, pageErrors };
};

export const processDataToDispatchErrorAction = (data: { payload?: any; actionMgrID?: number; type?: string }) => {
  const { payload } = data;
  const { err, context, actionMgrID } = payload;
  const error = err && err.response ? err.response.data : '';
  const formFields = getVisibleFormFields(context);
  // Since an error has occurred, try to get the actionMgrID from the payload
  //  and reject that entry's promise before returning...
  if (actionMgrID) {
    ActionManagerInstance.rejectActionByID(actionMgrID, true, err);
  }

  // payload with all errors (app, page, field)
  return actions.postProcess(actions.error, 'Error action triggered', error, context, formFields);
};

export const getActionBasedOnContainerMode = (target: string | null, payload: any) => {
  if (isContainerSingleMode(target) && hasContainerItems(target)) {
    return actions.postProcess(actions.replaceContainerItem, payload);
  }
  return actions.postProcess(actions.addContainerItem, payload);
};

export const getContext = (
  target: string | null,
  containerName: string,
  context = constants.APP.APP,
  options: { [key: string]: any } = {}
): string | null => {
  let currentContext: string | null = null;
  const { buildNew = true } = options;
  if (isContainerSingleMode(target)) {
    if (hasContainerItems(target)) {
      currentContext = getActiveContainerItemName(target);
    } else {
      currentContext = buildContextName(containerName, context);
    }
  } else if (isContainerMultipleMode(target)) {
    if (hasContainerItems(target) && !buildNew) {
      currentContext = getActiveContainerItemContext(target);
    } else if (!hasContainerItems(target) || buildNew) {
      currentContext = buildContextName(containerName, context);
    }
  }
  return currentContext;
};

export const isAddContainerAction = (action: { type: string }) => {
  return action && action.type === ADD_CONTAINER_ITEM;
};

export const areChildContextsInDirtyState = (parentContainerItemID: any) => {
  let isDirty = false;
  const childContainersContexts = ContainerUtils.getChildContainersContexts(parentContainerItemID);
  for (let index = 0; index < childContainersContexts.length; index += 1) {
    const childContext = childContainersContexts[index];
    if (childContext && isContextDirty(childContext)) {
      isDirty = true;
      break;
    }
  }
  return isDirty;
};

/**
 * This function will help to get dirty details of target passed and checks for dirty based on container type
 * -- if container type is single - it checks whether active container is in dirty state or not. If dirty it prompts a window confirmation
 *    based on user action, continueExecution will be set.
 * -- if container type is multiple - it checks whether container limit has reached or not.
 *        a) if container limit is not reached then continueExecution will be true.
 *        b) if container limit is reached and found non-dirty container then continueExecution will be true and removeActionPayload contains
 the containerItem details which need to be removed.
 *        c) if container limit is reached and all are dirty containers then continueExecution will be false and
 *              window alert will be shown on UI.
 * @param target - target container e.g. app/primary
 * @param options - additional information for proccessing logic. e.g. showConfirmDialogForMaxDocumentLimitReached
 * @returns - object contains below details
 1) continueExecution (boolean type) - a) true indicates to continue execution
 - b) false indicates to stop further execution
 2) removeActionPayload (object) - will have details related to container which need to be removed.

 @private
 */
// eslint-disable-next-line sonarjs/cognitive-complexity
export const getDirtyDetails = (target: string | null, options: { [key: string]: any } = {}) => {
  let showDirtyDialog = false;
  let showMaxDocumentLimitReachedDialog = false;
  let removeActionPayload: { [key: string]: any } = {};
  let continueExecution = true;
  let dirtyDialogStatus = false;
  let maxDocumentLimitReachedDialogStatus = false;
  /* we will be not using this flag for now, as we are going with infinity behavior as default */
  const { showConfirmDialogForMaxDocumentLimitReached = false, dirtyMessage, skipChildDirtyState = false } = options;

  const localeUtils = getLocaleUtils();

  if (ContainerUtils.isContainerSingleMode(target)) {
    const activeContainerName = ContainerUtils.getActiveContainerItemName(target);
    if (!activeContainerName) {
      return { continueExecution, removeActionPayload };
    }
    const dataContextName = ContainerUtils.getDataContextName(activeContainerName);
    if (isContextDirty(dataContextName)) {
      showDirtyDialog = true;
    }

    if (!skipChildDirtyState && !showDirtyDialog) {
      showDirtyDialog = areChildContextsInDirtyState(activeContainerName);
    }
  } else if (ContainerUtils.isContainerMultipleMode(target) && ContainerUtils.isContainerLimitReached(target)) {
    let leastRecentlyUsedContainerItemID = ContainerUtils.getLeastRecentlyUsedNonDirtyContainerItemID(target);
    if (!leastRecentlyUsedContainerItemID) {
      leastRecentlyUsedContainerItemID = ContainerUtils.getActiveContainerItemName(target);
      showMaxDocumentLimitReachedDialog = true;
    }
    removeActionPayload.containerItemID = leastRecentlyUsedContainerItemID;
    removeActionPayload.context = ContainerUtils.getDataContextName(leastRecentlyUsedContainerItemID);
  }

  /* Show Dirty Dialog in case of Single Mode Container Type */
  if (showDirtyDialog) {
    const msg = dirtyMessage || FORM_DIRTY_MESSAGE;
    // eslint-disable-next-line no-alert
    dirtyDialogStatus = window.confirm(localeUtils.getLocaleValue(msg, 'Messages'));
  }

  if (!dirtyDialogStatus && showDirtyDialog) {
    removeActionPayload = {};
    continueExecution = false;
  }

  /* Show Max Document Reached Limit Dialog in case of Multiple Mode Container Type */
  if (showMaxDocumentLimitReachedDialog) {
    const msg = localeUtils.getLocaleValue(MAX_DOCUMENT_LIMIT_REACHED, 'Messages');
    if (showConfirmDialogForMaxDocumentLimitReached) {
      // eslint-disable-next-line no-alert
      maxDocumentLimitReachedDialogStatus = window.confirm(msg);
    } else {
      // eslint-disable-next-line no-alert
      window.alert(msg);
      continueExecution = false;
    }
  }

  if (!maxDocumentLimitReachedDialogStatus && showMaxDocumentLimitReachedDialog) {
    removeActionPayload = {};
    continueExecution = false;
  }

  return { continueExecution, removeActionPayload };
};

const getContainerResourceType = (target: string | null, containerItemName: string | null) => {
  const containerItemData = ContainerUtils.getContainerItemData(target, containerItemName);
  return containerItemData?.resourceType;
};

/**
 * This function will help to get activation details of ASSIGNMENT type ITEM based on config details
 * @param target - target container e.g. app/primary
 * @param config - contains information related to ASSIGNMENT e.g. semanticURL, inskey
 * @returns - object contains below details
 1) activate (boolean type) - a) true indicates if ITEM is already present
 - b) false indicates if ITEM is not present
 2) activateContainerActionPayload (object) - will have details related to container in which ITEM is present

 @private
 */
const getAssignmentActivationDetails = (
  target: string | null,
  config: { insKey: any; isParallelCall: any; skipFlowNameCheck: any; type?: string }
) => {
  let activate = false;
  const isCoexistenceUIKITApp = EnvironmentInfo.isCoexistenceAppOfType(constants.APP_TYPE.UIKIT);
  const { insKey, isParallelCall, skipFlowNameCheck } = config;

  if (!insKey) return {};
  const [, flowName] = insKey.split('!') || '';
  const key = skipFlowNameCheck ? insKey : getWorkIDFromInsKey(insKey);
  const containerItemName = ContainerUtils.getContainerItemName(target, key);

  let containerItemData: { [key: string]: any } = {};
  let isParallelAssignment = false;

  if (
    containerItemName &&
    !isCoexistenceUIKITApp &&
    getContainerResourceType(target, containerItemName) === config?.type
  ) {
    activate = true;
    containerItemData = ContainerUtils.getContainerItemData(target, containerItemName);

    if (!skipFlowNameCheck) {
      let existingFlowName = containerItemData?.flowName;
      const containerName = getContainerNameFromContainerItemID(containerItemName);
      const childContainers = ContainerUtils.getChildContainerItems(containerItemName);
      if (childContainers.length) {
        const childTarget = getResolvedTargetName(containerName, childContainers[0]);
        existingFlowName = ContainerUtils.getContainerItemData(childTarget, childContainers[0])?.flowName;
      } else {
        activate = existingFlowName === flowName;
      }
      if (existingFlowName !== flowName || isParallelCall) {
        isParallelAssignment = true;
      }
    }
  }

  return {
    activate,
    activateContainerActionPayload: {
      target,
      isParallelAssignment,
      containerItemID: containerItemName,
      semanticURL: containerItemData.semanticURL,
      insKey
    }
  };
};

/**
 * This function will help to get activation details of PAGE type ITEM based on config details
 * @param target - target container e.g. app/primary
 * @param config - contains information related to PAGE e.g. semanticURL, inskey
 * @returns - object contains below details
 1) activate (boolean type) - a) true indicates if ITEM is already present
 - b) false indicates if ITEM is not present
 2) activateContainerActionPayload (object) - will have details related to container in which ITEM is present

 @private
 */
const getPageActivationDetails = (target: string | null, config: { [key: string]: any } = {}) => {
  let activate = false;
  const { semanticURL } = config;
  const containerItemName = ContainerUtils.getContainerItemName(target, semanticURL);

  if (containerItemName) {
    activate = true;
  }

  return {
    activate,
    activateContainerActionPayload: {
      target,
      containerItemID: containerItemName,
      semanticURL
    }
  };
};

/**
 * This function will help to get activation details of item based on config details (PAGE or ASSIGNMENT)
 * @param type - PAGE or ASSIGNMENT
 * @param target - target container e.g. app/primary
 * @param config - contains information related to PAGE or ASSIGNMENT e.g. semanticURL, inskey
 * @returns - object contains below details
 1) activate (boolean type) - a) true indicates if ITEM is already present
 - b) false indicates if ITEM is not present
 2) activateContainerActionPayload (object) - will have details related to container in which ITEM is present

 @private
 */

type ActivationDetails = {
  activate?: boolean;
  activateContainerActionPayload?: {
    target: string | null;
    isParallelAssignment?: boolean;
    containerItemID: string | null;
    semanticURL: string;
    insKey?: string;
  };
};

export const getActivationDetails = (type: string, target: string | null, config: any): ActivationDetails => {
  if (type === RESOURCE_TYPES.PAGE) {
    return getPageActivationDetails(target, config);
  }
  if (type === RESOURCE_TYPES.ASSIGNMENT) {
    return getAssignmentActivationDetails(target, { ...config, type });
  }
  if (type === RESOURCE_TYPES.CASE || type === RESOURCE_TYPES.OBJECT) {
    return getAssignmentActivationDetails(target, { ...config, skipFlowNameCheck: true, type });
  }
  return {};
};

/**
 * Returns the semanticURL for a showPage action along with whether the page
 * is the default portal nav page. Always calls getSemanticURL to preserve
 * its side effect (sets defaultClassName).
 */
const getShowPageSemanticDetails = (
  page: string,
  pageClass: string
): { semanticURL: string; isDefaultNavPage: boolean } => {
  const semanticURL = getSemanticURL(actionType.ACTION_SHOWVIEW, { page, pageClass });
  const navPages = StateManager.getStore()?.getState()?.data?.app?.pyPortal?.pyPrimaryNavPages;
  const defaultNavPage = Array.isArray(navPages) ? navPages[0] : undefined;
  const isDefaultNavPage = !!(defaultNavPage?.pyRuleName === page && defaultNavPage?.pyClassName === pageClass);
  return { semanticURL, isDefaultNavPage };
};

/**
 * Gets the name of a keyless container item with resourceType PAGE under the given target.
 * Used to locate the default nav page item created by showHistory without a key.
 */
const getKeylessPageContainerItemName = (target: string): string | null => {
  const containerItems = ContainerUtils.getContainerItems(target);
  if (!containerItems) return null;
  for (const [itemKey, item] of Object.entries(containerItems)) {
    if (!item.key && item.resourceType === publicConstants.PAGE) {
      return itemKey;
    }
  }
  return null;
};

/**
 * For the default nav page (loaded by showHistory without a key), gets the
 * keyless PAGE container item and returns activation details.
 */
const getDefaultPageActivationDetails = (
  target: string | null
): { activate: boolean; activateContainerActionPayload?: ActivationDetails['activateContainerActionPayload'] } => {
  if (target) {
    const containerItemName = getKeylessPageContainerItemName(target);
    if (containerItemName) {
      return {
        activate: true,
        activateContainerActionPayload: { target, containerItemID: containerItemName, semanticURL: '' }
      };
    }
  }
  return { activate: false };
};

/**
 * Resolves semanticURL and checks if the page can be activated (existing container item).
 * Keeps the original key-based activation for non-default pages, and adds a fallback
 * for the default nav page whose container item was created without a key by showHistory.
 */
export const resolveShowPageActivation = (
  page: string,
  pageClass: string,
  target: string | null
): {
  semanticURL: string;
  activate: boolean;
  activateContainerActionPayload?: ActivationDetails['activateContainerActionPayload'];
} => {
  const { semanticURL, isDefaultNavPage } = getShowPageSemanticDetails(page, pageClass);

  if (target && semanticURL && !isDefaultNavPage) {
    const pageActivationDetails = getActivationDetails(RESOURCE_TYPES.PAGE, target, { semanticURL });
    if (pageActivationDetails.activate) {
      return {
        semanticURL,
        activate: true,
        activateContainerActionPayload: pageActivationDetails.activateContainerActionPayload
      };
    }
  }

  if (isDefaultNavPage) {
    const defaultPageActivationDetails = getDefaultPageActivationDetails(target);
    if (defaultPageActivationDetails.activate) {
      return { semanticURL, ...defaultPageActivationDetails };
    }
    return { semanticURL: '', activate: false };
  }

  return { semanticURL, activate: false };
};

/**
 * This function is used to perform resetContainers as a batch action
 * @returns - returns a action object with type "RESET_CONTAINER"
 @private
 */
export const performResetContainer = () => {
  return actions.postProcess(
    actions.resetContainers,
    ContainerUtils.resetContainersPayload({
      context: publicConstants.APP.APP,
      name: constants.PREVIEW
    })
  );
};

/**
 * This function is used to perform merge content of current container to target inside target.data.caseinfo
 * @param target - target object
 * @param containerInfo - target object to be merged
 * @returns - returns merged object
 * @private
 */

export const mergeCurrentModalContentToTarget = (
  target: DxAPIResponseObject,
  containerInfo: { caseInfo: { content: any } }
) => {
  if (target?.data?.caseInfo && containerInfo?.caseInfo?.content) {
    target.data.caseInfo.content = containerInfo?.caseInfo?.content;
  }
  return target;
};

/**
 * This function will return the resetContainer Action if we have any Preview Containers Exist else return null
 * @param theReturn - An array which contains all the actions which needs to be dispatched.
 * @private
 */

export const addPreviewResetAction = (theReturn: any[] = []) => {
  if (hasContainerItems(`${constants.APP.APP}/${constants.PREVIEW}`)) {
    theReturn.push(performResetContainer());
  }
};

/**
 * This function will return the resourcetype (i.e PAGE or CASE or ASSIGNMENT) based on the routeKey of the url
 * @param routeKey - routeKey associated with the url.
 * @returns - type of resource i.e PAGE or CASE or ASSIGNMENT
 * @private
 */
export const getResourceType = (routeKey: string): string | undefined => {
  let actionCategory;
  for (const key of Object.keys(RESOURCE_TYPE_ACTIONS_MAP)) {
    if (RESOURCE_TYPE_ACTIONS_MAP[key].includes(routeKey)) {
      actionCategory = key;
      break;
    }
  }
  return actionCategory;
};

export const addActionsToParentContext = (
  childPayload: {
    data?: Record<string, unknown> | undefined;
    context: string;
    caseViewMode: string;
    skipMergeAssignmentsAndActions?: boolean;
  },
  reduxActions: object[],
  uiRootReference: string = '',
  mergeOptions: { propertiesToReplace?: { path: string; allowMultiple: boolean }[] } = {}
) => {
  const { data = {}, context, caseViewMode, skipMergeAssignmentsAndActions = true } = childPayload;
  const parentContext = getParentContextName(context);
  const parentPayload = {
    data: { ...data },
    caseViewMode,
    context: parentContext
  };
  /* remove shared node, as the shared data pages are not being requested by parent here */
  Utils.deleteIn(parentPayload.data, 'shared');
  Utils.setIn(parentPayload.data, ['context_data', 'caseViewMode'], caseViewMode);
  reduxActions.push({
    type: actionType.DELETE_CASE_SUMMARY,
    payload: {
      context: parentPayload.context,
      skipMergeAssignmentsAndActions
    }
  });

  const isUiRootFullPageRefPath =
    uiRootReference &&
    ![publicConstants.CASE_INFO.CASE_INFO_CONTENT, publicConstants.DATA_INFO.DATA_INFO_CONTENT].includes(
      uiRootReference
    );

  const { propertiesToReplace = [] } = mergeOptions;
  reduxActions.push(
    actions.postProcess(
      actions.mergeData,
      {
        ...parentPayload,
        ...(isUiRootFullPageRefPath && { uiRootReference })
      },
      ...(propertiesToReplace.length > 0 ? [{ propertiesToReplace }] : [])
    )
  );
};
const getActionInfo = (
  childOrRemoteKey: any,
  data: { payload: { isProcessAction: any; nextAssignmentInfo: { className: any } } },
  assignmentContext: string
) => {
  return {
    assignmentID: childOrRemoteKey,
    vcName: constants.PRIMARY,
    targetContainerName: Utils.getIn(data, ['payload', 'target']),
    context: Utils.getIn(data, ['payload', 'context']),
    ...(assignmentContext === 'other' && { isChild: true }),
    isProcessAction: data?.payload?.isProcessAction,
    ...(assignmentContext === 'remote' && { isRemote: true }),
    ...(assignmentContext === 'remote' && {
      remoteClassName: data.payload.nextAssignmentInfo.className
    })
  };
};
const getChildOrRemoteContextAction = (theReturn: any[]) => {
  const data = theReturn[0];
  for (const item of theReturn) {
    if (
      item?.payload?.nextAssignmentInfo?.context === 'other' ||
      item?.payload?.nextAssignmentInfo?.context === 'remote'
    ) {
      return item;
    }
  }
  return data;
};
export const dispatchCorrectActionIfChildOrRemote = (theReturn: any) => {
  if (Array.isArray(theReturn) && theReturn.length > 0) {
    const data = getChildOrRemoteContextAction(theReturn);
    const assignmentContext = Utils.getIn(data, ['payload', 'assignmentContext']);
    if (assignmentContext === 'other' || assignmentContext === 'remote') {
      const childOrRemoteKey = Utils.getIn(data, ['payload', 'nextAssignmentInfo', 'ID']);
      if (data?.payload?.isProcessAction) {
        const cancelActionInfo = {
          acName: Utils.getIn(data, ['payload', 'target']),
          caseID: data.payload.data.caseInfo.ID,
          containerItemID: Utils.getIn(data, ['payload', 'context']),
          context: Utils.getIn(data, ['payload', 'context'])
        };
        const cancelAssignment = {
          type: actionType.CANCEL_ASSIGNMENT,
          payload: { ...cancelActionInfo }
        };

        theReturn.push(cancelAssignment);

        // This filter should be removed when BUG-663687 is resolved
        theReturn = theReturn.filter((action) => delete action?.payload?.data?.caseMessages);

        StateManager.dispatch(theReturn);
        theReturn = theReturn.filter((action: { type: string }) => action.type !== actionType.CANCEL_ASSIGNMENT);
      }
      // Dispatch all the previous actions as well as along with child case open assignment action.
      /* Other actions are required to make sure the previous rendered assignment is not more visible
               and ToDo list is shown in case if the child case is create stage based case
            */
      const actionInfo: any = getActionInfo(childOrRemoteKey, data, assignmentContext);
      const isSingleDoc = ContainerUtils.isContainerSingleMode(actionInfo.targetContainerName);
      if (!isSingleDoc && data?.payload?.containerName === constants.WORKAREA) {
        actionInfo.isMultiDoc = true;
        theReturn = [];
      }
      const childOrRemoteCaseOpenAssignmentAction = {
        type: actionType.OPEN_ASSIGNMENT,
        payload: { ...actionInfo }
      };
      theReturn.push(childOrRemoteCaseOpenAssignmentAction);
      StateManager.dispatch(theReturn);
    } else {
      StateManager.dispatch(theReturn as any);
    }
  }
};

export const getUpdatedPayloadBasedOnResourceType = (
  resourceType: string | undefined,
  data: {
    caseInfo: { assignments: { ID: any }[]; ID: any };
    objectInfo: { ID: any };
    semanticURL: any;
  },
  payload: any
) => {
  if (
    resourceType === RESOURCE_TYPES.CASE ||
    resourceType === RESOURCE_TYPES.OBJECT ||
    resourceType === RESOURCE_TYPES.ASSIGNMENT
  ) {
    if (data?.caseInfo?.assignments && data?.caseInfo?.assignments.length > 0) {
      const { key, flowName } = getKeyAndFlowName(data.caseInfo.assignments[0].ID);
      payload.key = key || data.caseInfo.ID;
      payload.flowName = flowName;
    } else {
      payload.flowName = '';
      payload.key = data?.caseInfo?.ID || data?.objectInfo?.ID || data.semanticURL;
    }
    payload.resourceType = resourceType;
    payload.semanticURL = data.semanticURL;
    if (resourceType === RESOURCE_TYPES.ASSIGNMENT) {
      payload.caseViewMode = constants.PERFORM_MODE;
      payload.resourceType = RESOURCE_TYPES.CASE;
    } else {
      payload.caseViewMode = constants.REVIEW_MODE;
    }
  } else {
    payload.resourceType = resourceType;
    payload.semanticURL = data.semanticURL;
  }
  return payload;
};

/**
 * This function merges Objects and concats Arrays
 * @example
 * let object = { a: [{ b: 2 }], d: { x: 2 } };
 * let other = { a: [{ c: 3 }], d: { y: 3 } };
 * mergeObjectsAndConcatArrays(object, other) = { a: [{ b: 2 }, { c: 3 }], d: { x: 2, y: 3 } };
 */

export const mergeObjectsAndConcatArrays = (originalObj: { [key: string]: any }, newObj: { [key: string]: any }) => {
  function concatIfArray(objValue: string | any[], srcValue: any) {
    if (Array.isArray(objValue)) {
      return objValue.concat(srcValue);
    }
  }

  return merge(originalObj, newObj, concatIfArray);
};

export const getParentContextChangeSet = (context: any) => {
  let result = {};
  let parentContext = ContainerUtils.getContainerParentContext(context);
  /** ex: app/primary_1/workarea_1 <-parent- app/primary_1/workarea_1/modal_1 <-parent- app/primary_1/workarea_1/modal_1/modal_1 */
  while (parentContext) {
    const parentContextChangeSet = getSubmitData(parentContext);
    result = mergeObjectsAndConcatArrays(parentContextChangeSet.content, result);
    parentContext = ContainerUtils.getContainerParentContext(parentContext);
  }

  return result;
};

export const updateTraditionalRemoteCaseAction = (
  type: string,
  data: { activateContainerActionPayload: { [key: string]: string } },
  actionConfig: ExtendedActionObject,
  target: string | null
) => {
  let remoteCasePayload: ExtendedActionObject = {};
  let updatedType: string = type;
  if (actionConfig.isTraditionalRemoteCase) {
    remoteCasePayload = {
      ...actionConfig
    };

    // if containerItemID exists within the target to the corresponding key, change type to ACTIVATE_CONTAINER_ITEM
    const containerItemID: string | null = ContainerUtils.getContainerItemName(target, remoteCasePayload.insKey);
    if (containerItemID) {
      data.activateContainerActionPayload = {
        containerItemID
      };
      updatedType = actionType.ACTIVATE_CONTAINER_ITEM;
    }
  }

  return { remoteCasePayload, updatedType };
};

export const setTraditionalRemoteCasePayloadParams = (
  actionConfig: ExtendedActionObject,
  remoteCasePayload: ExtendedActionObject,
  payload: {
    resourceType: string;
    key: string | undefined;
    data: {
      caseInfo: RemoteCaseInfo;
    };
    target: string;
    containerItemID: string;
    isTraditionalRemoteCase: boolean;
    className?: string;
  }
) => {
  if (actionConfig.isTraditionalRemoteCase) {
    payload.resourceType = publicConstants.CASE;
    payload.key = CaseUtils.getRemoteCaseKey(remoteCasePayload.assignmentID, remoteCasePayload.insKey);
    // adding remotecase payload to caseInfo redux, along with classID ie., consumer class name
    payload.data.caseInfo = {
      ...payload.data.caseInfo,
      ...remoteCasePayload,
      classID: remoteCasePayload.className,
      target: payload.target,
      containerItemID: payload.containerItemID,
      caseTypeID: remoteCasePayload.className,
      content: {
        pyLabel: remoteCasePayload.name,
        pyID: getDisplayCaseID(CaseUtils.getRemoteCaseKey(remoteCasePayload.assignmentID, remoteCasePayload.insKey))
      }
    };

    payload.isTraditionalRemoteCase = true;
    payload.className = remoteCasePayload.className;
  }
};

export const handleLaunchAssignmentError = (context: string, theReturn: Actionout[]) => {
  let errorMessage = NavigationUtils.getComponentCache('assignments-launchAssignment-Error') || {};

  if (errorMessage && typeof errorMessage === 'object' && Object.keys(errorMessage).length > 0) {
    errorMessage = {
      ...errorMessage,
      context
    };

    theReturn.push(actions.postProcess(actions.addMessages, errorMessage));
    NavigationUtils.resetComponentCache('assignments-launchAssignment-Error');
  }
};

export const setCoexistenceAction = (routeKey: string, translatedDXAPIResponse: DxAPIResponseObject) => {
  const caseInfo = translatedDXAPIResponse?.data?.caseInfo;

  if (
    routeKey === actionType.ACTION_LAUNCH_ASSIGNMENT &&
    caseInfo?.caseTypeID &&
    caseInfo?.content &&
    isCoexistenceCase(caseInfo.caseTypeID)
  ) {
    caseInfo.content.coexistenceAction = ACTION_OPENASSIGNMENT;
  }
};

export const updateTraditionalGadgetId = (
  activateContainerActionPayload: ActivationDetails['activateContainerActionPayload']
) => {
  if (activateContainerActionPayload?.containerItemID) {
    // Updates traditionalGadgetId to ensure that the gadget reloads even if the same page is being activated again
    StateUtils.updateState(
      activateContainerActionPayload.containerItemID,
      'traditionalGadgetId',
      CaseUtils.getUniqueGadgetId(),
      {
        skipDirtyValidation: true
      }
    );
  }
};
